import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as SourcesActions from '../actions/sources-actions';
import * as UiActions from '../actions/ui-actions';
import { Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { OdataService } from '@app/Services/o-data.service';
import { AlertService, AlertType } from '@app/Services/Utilities/alert.service';
import { SourceData } from '@app/Shared/interfaces/iSources';

@Injectable()
export class SourcesEffects {
    constructor(
        private actions$: Actions,
        private odataService: OdataService,
        private store: Store<AppState>,
        private alertService: AlertService
    ) {}

    /**
     * Map API field names to component field names
     * API: SrcNm, SrcTruid, SrcRcrdBusStatNm, RcrdCrtUsrId
     * Component: sourceName, sourceTruKey, status, sourceTruID
     */
    private mapApiToComponent(apiSource: any): SourceData {
        return {
            sourceTruKey: apiSource.SrcTruid || apiSource.SrcTruKey,
            sourceTruID: apiSource.SrcTruid || apiSource.SrcTruID,
            sourceName: apiSource.SrcNm || apiSource.sourceName,
            status: apiSource.SrcRcrdBusStatNm === 'Active' || apiSource.status === true || apiSource.SrcRcrdBusStatNm === true,
        };
    }

    /**
     * Map component field names to API field names
     */
    private mapComponentToApi(componentSource: SourceData): any {
        const apiData: any = {
            SrcNm: componentSource.sourceName,
        };

        // Only include SrcTruid if it exists (for updates)
        if (componentSource.sourceTruKey) {
            apiData.SrcTruid = componentSource.sourceTruKey;
        }

        // Map status to API format
        apiData.SrcRcrdBusStatNm = componentSource.status ? 'Active' : 'Inactive';

        // Include RcrdCrtUsrId if available (default to 'dbadmin' as shown in API example)
        apiData.RcrdCrtUsrId = 'dbadmin';

        return apiData;
    }
loadSources$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SourcesActions.loadSources),
            switchMap(() => {
                this.store.dispatch(UiActions.startLoading());
                return this.odataService.getSources().pipe(
                    map((sources: any[]) => {
                        const mappedSources = sources.map((source: any) => this.mapApiToComponent(source));
                        return SourcesActions.loadSourcesSuccess({ sources: mappedSources });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(SourcesActions.loadSourcesFailure({ error: error.message || 'Failed to load sources' }));
                    })
                );
            })
        )
    );

    createSource$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SourcesActions.createSource),
            switchMap(({ sourceData }) => {
                this.store.dispatch(UiActions.startLoading());
                const apiData = this.mapComponentToApi(sourceData);
                return this.odataService.createSource(apiData).pipe(
                    map((response: any) => {
                        // Map the response back to component format
                        const createdSource = this.mapApiToComponent(response);
                        // Ensure sourceTruKey is set from response
                        if (response.SrcTruid) {
                            createdSource.sourceTruKey = response.SrcTruid;
                        }
                        return SourcesActions.createSourceSuccess({ source: createdSource });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(SourcesActions.createSourceFailure({ error: error.message || 'Failed to create source' }));
                    })
                );
            })
        )
    );
createSourceSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SourcesActions.createSourceSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Source created successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    createSourceFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SourcesActions.createSourceFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to create source: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );

    updateSource$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SourcesActions.updateSource),
            switchMap(({ key, sourceData }) => {
                this.store.dispatch(UiActions.startLoading());
                const apiData = this.mapComponentToApi(sourceData);
                return this.odataService.updateSource(key, apiData).pipe(
                    map((response: any) => {
                        // Map the response back to component format
                        const updatedSource = this.mapApiToComponent(response);
                        updatedSource.sourceTruKey = key;
                        return SourcesActions.updateSourceSuccess({ source: updatedSource });
                    }),
                    tap(() => this.store.dispatch(UiActions.stopLoading())),
                    catchError((error) => {
                        this.store.dispatch(UiActions.stopLoading());
                        return of(SourcesActions.updateSourceFailure({ error: error.message || 'Failed to update source' }));
                    })
                );
            })
        )
    );
updateSourceSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SourcesActions.updateSourceSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Source updated successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    updateSourceFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SourcesActions.updateSourceFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to update source: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );

    deleteSource$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SourcesActions.deleteSource),
            switchMap(({ key }) => {
                this.store.dispatch(UiActions.startLoading());
                // Note: The ODataService doesn't have a delete method yet, but we'll handle it here
                // For now, we'll dispatch success immediately since delete isn't implemented in the service
                // TODO: Implement deleteSource method in OdataService when DELETE endpoint is available
                this.store.dispatch(UiActions.stopLoading());
                return of(SourcesActions.deleteSourceSuccess({ key }));
            })
        )
    );
deleteSourceSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SourcesActions.deleteSourceSuccess),
                tap(() => {
                    this.alertService.notify(AlertType.Success, 'Source deleted successfully', 'Success');
                })
            ),
        { dispatch: false }
    );

    deleteSourceFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SourcesActions.deleteSourceFailure),
                tap(({ error }) => {
                    this.alertService.notify(AlertType.Error, `Failed to delete source: ${error}`, 'Error');
                })
            ),
        { dispatch: false }
    );
}
